import React from "react";
import { SECTION_GROUPS } from "../../data/posterFieldDescriptions";

/**
 * Inline SVG wireframe diagram of a poster layout in portrait orientation.
 * Shows labeled zones matching the section groups (A–I).
 */
export default function LayoutDiagram() {
  const W = 340;
  const H = 480;

  const margin = { top: 10, bottom: 16, side: 32 };
  const titleTop = margin.top + 20;
  const titleH = 30;
  const titleBottom = titleTop + titleH + 6;
  const dataTop = titleBottom + 8;
  const logoY = H - margin.bottom - 40;
  const dataBottom = logoY - 16;
  const logoH = 34;

  const contentL = margin.side;
  const contentR = W - margin.side;
  const contentW = contentR - contentL;
  const colGap = 8;
  const colW = (contentW - colGap) / 2;

  const colors = Object.fromEntries(SECTION_GROUPS.map((s) => [s.code, s.color]));

  function HArrow({ x1, x2, y, label, color }) {
    const mid = (x1 + x2) / 2;
    return (
      <g>
        <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth={0.8} />
        <line x1={x1} y1={y - 3} x2={x1} y2={y + 3} stroke={color} strokeWidth={0.8} />
        <line x1={x2} y1={y - 3} x2={x2} y2={y + 3} stroke={color} strokeWidth={0.8} />
        <text x={mid} y={y - 3} textAnchor="middle" fontSize={6} fill={color} fontWeight={600}>
          {label}
        </text>
      </g>
    );
  }

  function VArrow({ x, y1, y2, label, color, side = "left" }) {
    const mid = (y1 + y2) / 2;
    const tx = side === "left" ? x - 3 : x + 3;
    const anchor = side === "left" ? "end" : "start";
    return (
      <g>
        <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth={0.8} />
        <line x1={x - 3} y1={y1} x2={x + 3} y2={y1} stroke={color} strokeWidth={0.8} />
        <line x1={x - 3} y1={y2} x2={x + 3} y2={y2} stroke={color} strokeWidth={0.8} />
        <text x={tx} y={mid + 2} textAnchor={anchor} fontSize={6} fill={color} fontWeight={600}>
          {label}
        </text>
      </g>
    );
  }

  const sampleDateY = dataTop + 18;
  const sampleEntryY = sampleDateY + 28;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ maxWidth: 400, display: "block", margin: "0 auto" }}
      role="img"
      aria-label="Poster layout reference diagram"
    >
      <rect x={0} y={0} width={W} height={H} rx={4} fill="none" stroke="currentColor" strokeWidth={1.5} opacity={0.3} />

      {/* A — Page Canvas */}
      <rect x={1} y={1} width={W - 2} height={H - 2} rx={3} fill="none" stroke={colors.A} strokeWidth={1.5} strokeDasharray="4 2" />
      <HArrow x1={1} x2={W - 1} y={H - 3} label="A1 page_width" color={colors.A} />
      <VArrow x={W - 2} y1={1} y2={H - 1} label="A2 page_height" color={colors.A} side="right" />

      {/* B — Margins */}
      <rect x={0} y={0} width={W} height={margin.top} fill={colors.B} opacity={0.1} />
      <text x={W / 2} y={margin.top / 2 + 2} textAnchor="middle" fontSize={5} fill={colors.B} fontWeight={600}>B3 margin_top</text>
      <rect x={0} y={H - margin.bottom} width={W} height={margin.bottom} fill={colors.B} opacity={0.1} />
      <text x={W / 2} y={H - margin.bottom / 2 + 2} textAnchor="middle" fontSize={5} fill={colors.B} fontWeight={600}>B4 margin_bottom</text>
      <rect x={0} y={0} width={margin.side} height={H} fill={colors.B} opacity={0.08} />
      <text x={margin.side / 2} y={H / 2} textAnchor="middle" fontSize={5} fill={colors.B} fontWeight={600} transform={`rotate(-90 ${margin.side / 2} ${H / 2})`}>B1 margin_side</text>
      <rect x={W - margin.side} y={0} width={margin.side} height={H} fill={colors.B} opacity={0.08} />
      <text x={W - margin.side / 2} y={H / 2} textAnchor="middle" fontSize={5} fill={colors.B} fontWeight={600} transform={`rotate(90 ${W - margin.side / 2} ${H / 2})`}>B1 margin_side</text>

      {/* C — Title Area */}
      <rect x={contentL} y={titleTop} width={contentW} height={titleH + 12} rx={2} fill={colors.C} opacity={0.12} stroke={colors.C} strokeWidth={0.8} />
      <VArrow x={contentL - 6} y1={margin.top} y2={titleTop} label="C1" color={colors.C} />
      <text x={contentL + contentW / 2} y={titleTop + titleH / 2 + 2} textAnchor="middle" fontSize={8} fill={colors.C} fontWeight={700}>
        TITLE (C2 font)
      </text>
      <VArrow x={contentL - 6} y1={titleTop + titleH + 12} y2={dataTop} label="C3" color={colors.C} />

      {/* D — Data Columns */}
      <rect x={contentL} y={dataTop} width={contentW} height={dataBottom - dataTop} rx={2} fill="none" stroke={colors.D} strokeWidth={0.8} strokeDasharray="3 2" />
      <rect x={contentL + 2} y={dataTop + 2} width={colW - 2} height={dataBottom - dataTop - 4} rx={1} fill={colors.D} opacity={0.06} />
      <text x={contentL + colW / 2} y={dataTop + 12} textAnchor="middle" fontSize={5.5} fill={colors.D} fontWeight={600}>D1 portion_width</text>
      <rect x={contentL + colW + colGap} y={dataTop + 2} width={colW - 2} height={dataBottom - dataTop - 4} rx={1} fill={colors.D} opacity={0.06} />
      <text x={contentL + colW + colGap + colW / 2} y={dataTop + 12} textAnchor="middle" fontSize={5.5} fill={colors.D} fontWeight={600}>Column 2</text>
      <text x={contentL + contentW / 2} y={dataBottom - 4} textAnchor="middle" fontSize={5} fill={colors.D} fontWeight={600}>D3 nr_of_columns = 2</text>

      {/* E — Date Header */}
      <rect x={contentL + 4} y={sampleDateY} width={colW - 8} height={22} rx={1} fill={colors.E} opacity={0.12} stroke={colors.E} strokeWidth={0.5} />
      <text x={contentL + 10} y={sampleDateY + 8} fontSize={9} fill={colors.E} fontWeight={700}>15</text>
      <text x={contentL + 23} y={sampleDateY + 8} fontSize={5} fill={colors.E}>SAT (E1 day)</text>
      <text x={contentL + 10} y={sampleDateY + 16} fontSize={5} fill={colors.E}>March 2025 (E2 month)</text>
      <text x={contentL + colW - 12} y={sampleDateY + 8} textAnchor="end" fontSize={4.5} fill={colors.E} fontStyle="italic">E3 matchday</text>
      <VArrow x={contentL + colW - 4} y1={sampleDateY + 22} y2={sampleEntryY} label="E4" color={colors.E} side="right" />

      {/* F — Fixture Entry */}
      <rect x={contentL + 4} y={sampleEntryY} width={colW - 8} height={28} rx={1} fill={colors.F} opacity={0.1} stroke={colors.F} strokeWidth={0.5} />
      <text x={contentL + 8} y={sampleEntryY + 9} fontSize={5} fill={colors.F} fontWeight={600}>14:00</text>
      <line x1={contentL + 26} y1={sampleEntryY + 3} x2={contentL + 26} y2={sampleEntryY + 13} stroke={colors.F} strokeWidth={0.3} opacity={0.5} />
      <text x={contentL + 30} y={sampleEntryY + 9} fontSize={5} fill={colors.F}>Team A v Team B (F1)</text>
      <text x={contentL + 30} y={sampleEntryY + 17} fontSize={4} fill={colors.F} opacity={0.7}>Competition (F2 secondary)</text>
      <text x={contentL + 20} y={sampleEntryY + 9} fontSize={3.5} fill={colors.F} opacity={0.6}>F3</text>
      <VArrow x={contentL + colW - 4} y1={sampleEntryY + 28} y2={sampleEntryY + 38} label="F4" color={colors.F} side="right" />

      <rect x={contentL + 4} y={sampleEntryY + 38} width={colW - 8} height={14} rx={1} fill={colors.F} opacity={0.06} stroke={colors.F} strokeWidth={0.3} strokeDasharray="2 1" />
      <text x={contentL + colW / 2} y={sampleEntryY + 47} textAnchor="middle" fontSize={4} fill={colors.F} opacity={0.6}>next entry...</text>

      {/* G — Spacing */}
      <VArrow x={contentL + colW + colGap / 2} y1={sampleDateY + 22} y2={sampleDateY + 22 + 16} label="G1" color={colors.G} />
      <text x={contentL + colW + colGap / 2} y={sampleDateY + 22 + 22} textAnchor="middle" fontSize={4} fill={colors.G}>vertical spacing</text>
      <text x={contentL + colW + colGap + 6} y={sampleEntryY + 26} fontSize={4} fill={colors.G}>G2 line_height</text>

      {/* H — Logo Area */}
      <rect x={contentL} y={logoY} width={contentW} height={logoH} rx={2} fill={colors.H} opacity={0.12} stroke={colors.H} strokeWidth={0.8} />
      <text x={contentL + contentW / 2} y={logoY + logoH / 2 + 2} textAnchor="middle" fontSize={7} fill={colors.H} fontWeight={600}>
        LOGO (H1 position_y)
      </text>
      <VArrow x={contentL - 6} y1={dataBottom} y2={logoY} label="H2" color={colors.H} />

      {/* I — Split & Zoom */}
      <rect x={contentL + colW + colGap + 4} y={sampleEntryY + 60} width={colW - 8} height={36} rx={2} fill={colors.I} opacity={0.08} stroke={colors.I} strokeWidth={0.5} strokeDasharray="2 2" />
      <text x={contentL + colW + colGap + colW / 2} y={sampleEntryY + 72} textAnchor="middle" fontSize={5} fill={colors.I} fontWeight={600}>Split & Zoom (I)</text>
      <text x={contentL + colW + colGap + colW / 2} y={sampleEntryY + 80} textAnchor="middle" fontSize={4} fill={colors.I}>I1 min_entries_for_split</text>
      <text x={contentL + colW + colGap + colW / 2} y={sampleEntryY + 87} textAnchor="middle" fontSize={4} fill={colors.I}>I3 max_zoom_multiplier</text>

      {/* Legend */}
      {SECTION_GROUPS.map((s, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const lx = 12 + col * 115;
        const ly = H - 56 + row * 14;
        return (
          <g key={s.code}>
            <rect x={lx} y={ly} width={8} height={8} rx={1} fill={s.color} opacity={0.8} />
            <text x={lx + 11} y={ly + 7} fontSize={5.5} fill="currentColor" opacity={0.8}>
              {s.code} — {s.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
